'use client';
import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import Image from 'next/image';
import { LayoutDashboard, FileText, ClipboardList, Bell, DollarSign, Map, Scale, CheckSquare, ShieldCheck, BarChart3, ChevronLeft, ChevronRight, LogOut, } from 'lucide-react';

export interface SubMenuItem {
  label: string;
  icon: React.ElementType;
  href?: string;
  external?: boolean;
  badge?: string;
}

export interface ModuleItem {
  id: string;
  href: string;
  icon: React.ElementType;
  label: string;
  shortLabel: string;
  badge?: number;
  subMenus: SubMenuItem[];
}

import { Home, Layers, RefreshCw, AlertTriangle, TrendingUp, Gavel, Search, PlusCircle, Clock, ArrowRightCircle, XCircle, Send, Activity, CreditCard, Receipt, PieChart, MapPin, Maximize2, Download, ExternalLink, BookOpen, Award, History, UserCheck, FileCheck, AlertOctagon, Lock, Unlock, MessageSquare, FileMinus, FileSearch, Printer, TrendingDown, Building, Star, CalendarDays, StickyNote, Eye, Users, FileBarChart2, Briefcase, UserCog } from 'lucide-react';



export const MODULES: ModuleItem[] = [
  {
    id: 'dashboard',
    href: '/',
    icon: LayoutDashboard,
    label: 'Tableau de bord',
    shortLabel: 'Tableau',
    subMenus: [
      { label: 'Vue d\'ensemble KPIs', icon: Home, href: '/' },
      { label: 'Évolution 30 jours', icon: TrendingUp, href: '/' },
      { label: 'Alertes critiques', icon: AlertTriangle, href: '/centre-alertes' },
      { label: 'Décisions DG en attente', icon: UserCheck, href: '/validations-dg' },
      { label: 'Top sociétés minières', icon: Star, href: '/' },
      { label: 'Rapports express', icon: BarChart3, href: '/rapports-strategiques' },
    ],
  },
  {
    id: 'titres',
    href: '/titres-miniers',
    icon: FileText,
    label: 'Gestion des titres miniers',
    shortLabel: 'Titres',
    badge: 3,
    subMenus: [
      { label: 'Titres actifs', icon: FileCheck, href: '/titres-miniers' },
      { label: 'Titres expirés', icon: FileMinus, href: '/titres-miniers' },
      { label: 'Proche expiration', icon: Clock, href: '/titres-miniers' },
      { label: 'Nouvelle demande', icon: PlusCircle, href: '/titres-miniers' },
      { label: 'Fiche titre', icon: FileSearch, href: '/titres-miniers' },
      { label: 'Renouvellements', icon: RefreshCw, href: '/titres-miniers' },
      { label: 'Cession de titres', icon: ArrowRightCircle, href: '/titres-miniers' },
      { label: 'Recherche multi-critères', icon: Search, href: '/titres-miniers' },
    ],
  },
  {
    id: 'requetes',
    href: '/requetes-procedures',
    icon: ClipboardList,
    label: 'Nouvelles demandes et procédures',
    shortLabel: 'Demandes',
    badge: 23,
    subMenus: [
      { label: 'Nouvelles demandes reçues', icon: ClipboardList, href: '/requetes-procedures' },
      { label: 'En cours de traitement', icon: Activity, href: '/requetes-procedures' },
      { label: 'Approuvées', icon: CheckSquare, href: '/requetes-procedures' },
      { label: 'Rejetées', icon: XCircle, href: '/requetes-procedures' },
      { label: 'Transmises au cabinet', icon: Send, href: '/requetes-procedures' },
      { label: 'Délais critiques', icon: AlertOctagon, href: '/requetes-procedures', badge: '23' },
      { label: 'Traçabilité', icon: History, href: '/requetes-procedures' },
      { label: 'Créer nouvelle demande', icon: PlusCircle, href: '/requetes-procedures' },
    ],
  },
  {
    id: 'alertes',
    href: '/centre-alertes',
    icon: Bell,
    label: 'Centre d\'alertes',
    shortLabel: 'Alertes',
    badge: 7,
    subMenus: [
      { label: 'Alertes actives', icon: Bell, href: '/centre-alertes' },
      { label: 'Alertes critiques', icon: AlertOctagon, href: '/centre-alertes', badge: '7' },
      { label: 'Titres expirés', icon: FileMinus, href: '/centre-alertes' },
      { label: 'Délais dépassés', icon: Clock, href: '/centre-alertes' },
      { label: 'Paiements en retard', icon: CreditCard, href: '/centre-alertes' },
      { label: 'Dossiers incomplets', icon: FileSearch, href: '/centre-alertes' },
      { label: 'Empiètements GIS', icon: Layers, href: '/centre-alertes' },
      { label: 'Assigner alerte', icon: UserCheck, href: '/centre-alertes' },
    ],
  },
  {
    id: 'finance',
    href: '/gestion-financiere',
    icon: DollarSign,
    label: 'Recettes',
    shortLabel: 'Recettes',
    badge: 5,
    subMenus: [
      { label: 'Tableau de bord des recettes', icon: PieChart, href: '/gestion-financiere' },
      { label: 'Recettes', icon: TrendingUp, href: '/gestion-financiere/recettes' },
      { label: 'Quittances', icon: Receipt, href: '/gestion-financiere/quittances' },
      { label: 'Taux de recouvrement / impayés et alertes', icon: Activity, href: '/gestion-financiere/taux-recouvrement' },
      { label: 'Recettes par province', icon: MapPin, href: '/gestion-financiere/recettes-par-province' },
      { label: 'Budget vs réalisation', icon: BarChart3, href: '/gestion-financiere/budget-vs-realisation' },
      { label: 'Historique de paiement', icon: History, href: '/gestion-financiere/historique-paiement' },
    ],
  },
  {
    id: 'depenses',
    href: '/depenses',
    icon: TrendingDown,
    label: 'Dépenses',
    shortLabel: 'Dépenses',
    subMenus: [
      { label: 'Tableau de bord dépenses', icon: PieChart, href: '/depenses' },
      { label: 'Dépenses', icon: TrendingDown, href: '/depenses/depenses' },
      { label: 'Factures fournisseurs', icon: Receipt, href: '/depenses/factures-fournisseurs' },
      { label: 'État de somme', icon: BarChart3, href: '/depenses/etat-de-somme' },
      { label: 'Impayés', icon: AlertOctagon, href: '/depenses/impayes' },
      { label: 'Dépenses par province', icon: MapPin, href: '/depenses/depenses-par-province' },
      { label: 'Budget vs réalisé', icon: Activity, href: '/depenses/budget-vs-realite' },
      { label: 'Historique de dépenses', icon: History, href: '/depenses/historique-depenses' },
    ],
  },
  {
    id: 'gis',
    href: '/cartographie-gis',
    icon: Map,
    label: 'Cartographie GIS',
    shortLabel: 'GIS',
    subMenus: [
      { label: 'Carte interactive', icon: Map, href: '/cartographie-gis' },
      { label: 'Titres par province', icon: MapPin, href: '/cartographie-gis' },
      { label: 'Zones occupées', icon: Maximize2, href: '/cartographie-gis' },
      { label: 'Empiètements', icon: Layers, href: '/cartographie-gis' },
      { label: 'Titres en litige', icon: Scale, href: '/cartographie-gis' },
      { label: 'Exporter carte', icon: Download, href: '/cartographie-gis' },
      { label: 'Carte et OM 2022', icon: ExternalLink, href: 'https://www.cami.cd/index.php/carte-miniere', external: true },
    ],
  },
  {
    id: 'litiges',
    href: '/litiges-contentieux',
    icon: Scale,
    label: 'Litiges et contentieux',
    shortLabel: 'Litiges',
    subMenus: [
      { label: 'Litiges actifs', icon: Scale, href: '/litiges-contentieux' },
      { label: 'Oppositions', icon: XCircle, href: '/litiges-contentieux' },
      { label: 'Affaires judiciaires', icon: Gavel, href: '/litiges-contentieux' },
      { label: 'Recours administratifs', icon: BookOpen, href: '/recours-administratifs', badge: '12' },
      { label: 'Coûts et provisions', icon: DollarSign, href: '/litiges-contentieux' },
      { label: 'Tendances par province', icon: TrendingDown, href: '/litiges-contentieux' },
      { label: 'Nouveau litige', icon: PlusCircle, href: '/litiges-contentieux' },
      { label: 'Historique', icon: History, href: '/litiges-contentieux' },
    ],
  },
  {
    id: 'qualite',
    href: '/controle-qualite',
    icon: CheckSquare,
    label: 'Contrôle qualité',
    shortLabel: 'Qualité',
    subMenus: [
      { label: 'Dossiers incomplets', icon: FileMinus, href: '/controle-qualite' },
      { label: 'Anomalies cadastrales', icon: AlertOctagon, href: '/controle-qualite' },
      { label: 'Blocages en cours', icon: Lock, href: '/controle-qualite' },
      { label: 'Notifications envoyées', icon: MessageSquare, href: '/controle-qualite' },
      { label: 'Vérification automatique', icon: RefreshCw, href: '/controle-qualite' },
      { label: 'Rapport qualité', icon: Printer, href: '/controle-qualite' },
      { label: 'Historique contrôles', icon: History, href: '/controle-qualite' },
      { label: 'Respect délai fournisseur', icon: Clock, href: '/depenses/respect-delai-fournisseur' },
    ],
  },
  {
    id: 'validations',
    href: '/validations-dg',
    icon: ShieldCheck,
    label: 'Validation',
    shortLabel: 'Validations',
    badge: 8,
    subMenus: [
      { label: 'Décisions en attente', icon: Clock, href: '/validations-dg', badge: '8' },
      { label: 'Arbitrages', icon: Scale, href: '/validations-dg' },
      { label: 'Signatures et horodatage', icon: Award, href: '/validations-dg' },
      { label: 'Autorisations sensibles', icon: Unlock, href: '/validations-dg' },
      { label: 'Renouvellements stratégiques', icon: RefreshCw, href: '/validations-dg' },
      { label: 'Historique décisions', icon: History, href: '/validations-dg' },
      { label: 'Délégations', icon: UserCheck, href: '/validations-dg' },
      { label: 'Tableau de bord DG', icon: LayoutDashboard, href: '/validations-dg' },
    ],
  },
  {
    id: 'rapports',
    href: '/rapports-strategiques',
    icon: BarChart3,
    label: 'Rapports stratégiques',
    shortLabel: 'Rapports',
    subMenus: [
      { label: 'Rapport sectoriel', icon: BarChart3, href: '/rapports-strategiques' },
      { label: 'Évolution par province', icon: TrendingUp, href: '/rapports-strategiques' },
      { label: 'Sociétés les plus actives', icon: Building, href: '/rapports-strategiques' },
      { label: 'Graphiques exportables', icon: Download, href: '/rapports-strategiques' },
      { label: 'Rapport financier', icon: DollarSign, href: '/rapports-strategiques/rapport-financier' },
      { label: 'Rapport litiges', icon: Scale, href: '/rapports-strategiques' },
      { label: 'Rapport qualité', icon: CheckSquare, href: '/rapports-strategiques' },
      { label: 'Générer rapport', icon: Printer, href: '/rapports-strategiques' },
    ],
  },
  {
    id: 'visites',
    href: '/visites',
    icon: Eye,
    label: 'Missions',
    shortLabel: 'Missions',
    badge: 3,
    subMenus: [
      { label: 'Tableau de bord missions', icon: LayoutDashboard, href: '/visites' },
      { label: 'Missions planifiées', icon: CalendarDays, href: '/visites' },
      { label: 'Missions en cours', icon: Activity, href: '/visites' },
      { label: 'Rapports des missions', icon: FileBarChart2, href: '/visites' },
      { label: 'Planifier une mission', icon: PlusCircle, href: '/visites' },
      { label: 'Historique', icon: History, href: '/visites' },
    ],
  },
  {
    id: 'agenda',
    href: '/agenda',
    icon: CalendarDays,
    label: 'Agenda DG',
    shortLabel: 'Agenda',
    badge: 5,
    subMenus: [
      { label: 'Vue calendrier', icon: CalendarDays, href: '/agenda' },
      { label: 'Réunions du jour', icon: Users, href: '/agenda' },
      { label: 'Audiences et audiences', icon: Briefcase, href: '/agenda' },
      { label: 'Déplacements officiels', icon: MapPin, href: '/agenda' },
      { label: 'Rappels et échéances', icon: Clock, href: '/agenda' },
      { label: 'Ajouter événement', icon: PlusCircle, href: '/agenda' },
    ],
  },
  {
    id: 'memo',
    href: '/memo',
    icon: StickyNote,
    label: 'Mémo et notes DG',
    shortLabel: 'Mémo',
    subMenus: [
      { label: 'Mémos récents', icon: StickyNote, href: '/memo' },
      { label: 'Notes de service', icon: FileText, href: '/memo' },
      { label: 'Instructions DG', icon: ShieldCheck, href: '/memo' },
      { label: 'Note de service', icon: Send, href: '/memo' },
      { label: 'Communiqué', icon: MessageSquare, href: '/memo' },
      { label: 'Archivés', icon: History, href: '/memo' },
      { label: 'Nouveau mémo', icon: PlusCircle, href: '/memo' },
    ],
  },
  {
    id: 'substances',
    href: '/substances-minerales',
    icon: Layers,
    label: 'Substances minérales',
    shortLabel: 'Substances',
    subMenus: [
      { label: 'Tableau substances', icon: Layers, href: '/substances-minerales' },
      { label: 'Autorisations', icon: FileCheck, href: '/substances-minerales' },
      { label: 'Statistiques', icon: BarChart3, href: '/substances-minerales' },
    ],
  },
  {
    id: 'conformite',
    href: '/conformite-environnementale',
    icon: ShieldCheck,
    label: 'Conformité environnementale',
    shortLabel: 'Conformité',
    subMenus: [
      { label: 'Tableau de bord', icon: LayoutDashboard, href: '/conformite-environnementale' },
      { label: 'Engagements', icon: FileCheck, href: '/conformite-environnementale' },
      { label: 'Audits', icon: Search, href: '/conformite-environnementale' },
      { label: 'Conformité', icon: CheckSquare, href: '/conformite-environnementale' },
    ],
  },
  {
    id: 'commission',
    href: '/commission-travail',
    icon: Users,
    label: 'Commission de travail',
    shortLabel: 'Commission',
    badge: 4,
    subMenus: [
      { label: 'Tableau de bord', icon: LayoutDashboard, href: '/commission-travail' },
      { label: 'Commissions actives', icon: Users, href: '/commission-travail' },
      { label: 'Membres et mandats', icon: UserCheck, href: '/commission-travail' },
      { label: 'Réunions et PV', icon: CalendarDays, href: '/commission-travail' },
      { label: 'Rapports de commission', icon: FileBarChart2, href: '/commission-travail' },
      { label: 'Nouvelle commission', icon: PlusCircle, href: '/commission-travail' },
      { label: 'Historique', icon: History, href: '/commission-travail' },
    ],
  },
  {
    id: 'rh',
    href: '/rh',
    icon: UserCog,
    label: 'Ressources Humaines',
    shortLabel: 'RH',
    subMenus: [
      { label: 'Tableau de bord RH', icon: LayoutDashboard, href: '/rh' },
      { label: 'Effectif & Paie', icon: DollarSign, href: '/rh' },
      { label: 'Présences & Absences', icon: UserCheck, href: '/rh' },
      { label: 'Heures supplémentaires', icon: Clock, href: '/rh' },
      { label: 'Retards du mois', icon: AlertOctagon, href: '/rh' },
      { label: 'Synthèse RH', icon: BarChart3, href: '/rh' },
      { label: 'Historique RH', icon: History, href: '/rh' },
    ],
  },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  activeModuleId?: string;
  onModuleSelect?: (moduleId: string) => void;
}

export default function Sidebar({ collapsed, onToggle, activeModuleId, onModuleSelect }: SidebarProps) {
  const pathname = usePathname();

  const getActiveModule = () => {
    if (activeModuleId) return activeModuleId;
    const found = MODULES.find(m => {
      if (m.href === '/' && pathname === '/') return true;
      if (m.href !== '/' && pathname.startsWith(m.href)) return true;
      return false;
    });
    return found?.id || 'dashboard';
  };

  const currentActiveId = getActiveModule();

  return (
    <aside
      className={`flex flex-col h-screen sticky top-0 z-40 transition-all duration-300 ${
        collapsed ? 'w-16 min-w-[64px]' : 'w-64 min-w-[256px]'
      }`}
      style={{ background: '#0F2D48' }}
    >
      {/* Logo */}
      <div
        className={`flex items-center h-16 px-3 border-b shrink-0 ${collapsed ? 'justify-center' : 'gap-3'}`}
        style={{ borderColor: 'rgba(255,255,255,0.1)' }}
      >
        <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0 flex items-center justify-center bg-white">
          <Image
            src="/assets/images/image-1781505606681.png"
            alt="Logo CAMI — Cadastre Minier de la RDC"
            width={36}
            height={36}
            className="w-full h-full object-contain"
          />
        </div>
        {!collapsed && (
          <div>
            <span className="font-bold text-lg text-white tracking-tight">Poste de Travail DG</span>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Cadastre Minier</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {MODULES.map((module) => {
          const isActive = currentActiveId === module.id;
          const ModIcon = module.icon;
          return (
            <button
              key={module.id}
              onClick={() => onModuleSelect?.(module.id)}
              title={collapsed ? module.label : undefined}
              className={`w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 relative group ${
                isActive
                  ? 'text-white' :'hover:text-white'
              }`}
              style={{
                background: isActive ? '#1B4F72' : 'transparent',
                color: isActive ? '#fff' : 'rgba(189,195,199,0.85)',
                borderLeft: isActive ? '3px solid #D4AC0D' : '3px solid transparent',
              }}
            >
              <ModIcon size={18} className="shrink-0" />
              {!collapsed && (
                <>
                  <span className="truncate flex-1 text-left">{module.label}</span>
                  {module.badge !== undefined && (
                    <span
                      className="ml-auto text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[20px] text-center"
                      style={{ background: '#C0392B', color: '#fff' }}
                    >
                      {module.badge}
                    </span>
                  )}
                </>
              )}
              {collapsed && module.badge !== undefined && (
                <span
                  className="absolute top-0.5 right-0.5 w-4 h-4 text-[9px] font-bold rounded-full flex items-center justify-center"
                  style={{ background: '#C0392B', color: '#fff' }}
                >
                  {module.badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Bottom — Déconnexion only (Paramètres retiré) */}
      <div className="border-t p-2 space-y-0.5 shrink-0" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
        {/* DG profile mini */}
        {!collapsed && (
          <div className="flex items-center gap-2 px-2 py-2 mb-1">
            <div className="w-7 h-7 rounded-full overflow-hidden border shrink-0" style={{ borderColor: '#D4AC0D' }}>
              <Image
                src="/assets/images/image-1781505422790.png"
                alt="Popol Mabolia Yenga — DG CAMI"
                width={28}
                height={28}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-white truncate">Popol Mabolia Yenga</p>
              <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.4)' }}>Directeur Général</p>
            </div>
          </div>
        )}
        <button
          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm transition-all"
          style={{ color: 'rgba(189,195,199,0.7)' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#fff')}
          onMouseLeave={e => (e.currentTarget.style.color = 'rgba(189,195,199,0.7)')}
        >
          <LogOut size={16} className="shrink-0" />
          {!collapsed && <span>Déconnexion</span>}
        </button>
      </div>

      {/* Toggle */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-20 bg-white border border-gray-200 rounded-full w-6 h-6 flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors z-50"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed
          ? <ChevronRight size={12} style={{ color: '#1B4F72' }} />
          : <ChevronLeft size={12} style={{ color: '#1B4F72' }} />
        }
      </button>
    </aside>
  );
}